<script setup lang="ts">
/**
 * Blocking loading cover: a centred progress bar over a translucent scrim.
 *
 * `overlay`/`fixed` cover content that is already on screen; the default fills
 * the viewport for pages that have nothing to show yet.
 */
withDefaults(defineProps<{
  overlay?: boolean
  fixed?: boolean
}>(), {
  overlay: false,
  fixed: false,
})

const { t } = useKawadernaI18n()
</script>

<template>
  <div
    class="page-loading-cover"
    :class="{ 'is-overlay': overlay, 'is-fixed': fixed }"
    role="alertdialog"
    aria-busy="true"
    aria-live="assertive"
    :aria-label="t('common.loading')"
    @pointerdown.stop.prevent
    @click.stop.prevent
  >
    <div class="plc-panel">
      <span class="plc-mark" aria-hidden="true">
        <span class="plc-mark-ring" />
        <span class="plc-mark-core" />
      </span>
      <span class="plc-bar" aria-hidden="true">
        <span class="plc-bar-fill" />
      </span>
      <p class="plc-label">{{ t('common.loading') }}</p>
    </div>
  </div>
</template>

<style scoped>
.page-loading-cover {
  --plc-accent: #1e3a5f;
  --plc-accent-mid: #2c5282;
  --plc-accent-soft: #7aa4d6;
  display: grid;
  place-content: center;
  justify-items: center;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  padding: 1.5rem;
  background:
    radial-gradient(120% 85% at 50% 0%, rgba(44, 82, 130, 0.16), transparent 62%),
    rgba(248, 250, 252, 0.76);
  backdrop-filter: blur(10px) saturate(140%);
  -webkit-backdrop-filter: blur(10px) saturate(140%);
  box-sizing: border-box;
  pointer-events: auto;
  user-select: none;
  touch-action: none;
  animation: plc-fade 0.22s ease-out both;
}

.page-loading-cover.is-overlay,
.page-loading-cover.is-fixed {
  inset: 0;
  min-height: 0;
  background:
    radial-gradient(120% 85% at 50% 0%, rgba(44, 82, 130, 0.28), transparent 62%),
    rgba(15, 23, 42, 0.36);
}

.page-loading-cover.is-overlay {
  position: absolute;
  z-index: 80;
}

/* Above the tallest app overlays (admin panel sits at 12000). */
.page-loading-cover.is-fixed {
  position: fixed;
  z-index: 13000;
}

.plc-panel {
  position: relative;
  overflow: hidden;
  display: grid;
  justify-items: center;
  gap: 1rem;
  width: min(20rem, 100%);
  padding: 1.65rem 1.5rem 1.4rem;
  border-radius: 1.35rem;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(241, 245, 251, 0.93));
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow:
    0 26px 60px rgba(15, 23, 42, 0.3),
    0 2px 0 rgba(255, 255, 255, 0.9) inset;
  animation: plc-rise 0.34s cubic-bezier(0.2, 0.8, 0.25, 1) both;
}

/* Light sweeping across the glass so the card does not read as a flat box. */
.plc-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(105deg, transparent 32%, rgba(255, 255, 255, 0.6) 48%, transparent 64%);
  transform: translateX(-120%);
  animation: plc-sheen 2.6s ease-in-out infinite;
  pointer-events: none;
}

.plc-mark {
  position: relative;
  display: grid;
  place-items: center;
  width: 2.9rem;
  height: 2.9rem;
}

.plc-mark-ring {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: conic-gradient(
    from 0deg,
    rgba(44, 82, 130, 0) 0deg,
    rgba(44, 82, 130, 0.15) 120deg,
    var(--plc-accent-mid) 300deg,
    var(--plc-accent) 360deg
  );
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3.5px), #000 calc(100% - 3px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 3.5px), #000 calc(100% - 3px));
  animation: plc-spin 0.95s linear infinite;
}

.plc-mark-core {
  width: 0.62rem;
  height: 0.62rem;
  border-radius: 999px;
  background: linear-gradient(140deg, var(--plc-accent-mid), var(--plc-accent));
  box-shadow: 0 0 0 0 rgba(44, 82, 130, 0.45);
  animation: plc-core 1.9s ease-out infinite;
}

.plc-bar {
  position: relative;
  display: block;
  width: 100%;
  height: 0.45rem;
  border-radius: 999px;
  background: rgba(30, 58, 95, 0.1);
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.09);
  overflow: hidden;
}

.plc-bar-fill {
  position: absolute;
  inset-block: 0;
  width: 42%;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    rgba(44, 82, 130, 0) 0%,
    var(--plc-accent-soft) 28%,
    var(--plc-accent-mid) 55%,
    var(--plc-accent) 78%,
    rgba(30, 58, 95, 0) 100%
  );
  animation: plc-slide 1.3s cubic-bezier(0.65, 0.08, 0.34, 1) infinite;
}

.plc-label {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #475569;
}

@keyframes plc-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes plc-rise {
  from { opacity: 0; transform: translateY(0.75rem) scale(0.96); }
  to { opacity: 1; transform: none; }
}

@keyframes plc-sheen {
  0% { transform: translateX(-120%); }
  55%, 100% { transform: translateX(120%); }
}

@keyframes plc-spin {
  to { transform: rotate(360deg); }
}

@keyframes plc-core {
  0% { box-shadow: 0 0 0 0 rgba(44, 82, 130, 0.4); }
  70% { box-shadow: 0 0 0 0.7rem rgba(44, 82, 130, 0); }
  100% { box-shadow: 0 0 0 0 rgba(44, 82, 130, 0); }
}

@keyframes plc-slide {
  0% { left: -45%; }
  100% { left: 100%; }
}

@media (prefers-reduced-motion: reduce) {
  .page-loading-cover,
  .plc-panel {
    animation: none;
  }

  .plc-panel::after,
  .plc-mark-core {
    display: none;
  }

  .plc-mark-ring {
    animation-duration: 2.4s;
  }

  .plc-bar-fill {
    left: 0;
    width: 100%;
    animation: plc-pulse 1.8s ease-in-out infinite;
  }

  @keyframes plc-pulse {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
  }
}
</style>
